import { Link } from "react-router-dom";
import { Check, X as XIcon, ShieldCheck, AlertTriangle, Award, ArrowRight, Download } from "lucide-react";
import SEO, { serviceSchema, breadcrumbSchema, localBusinessSchema } from "../components/SEO";
import { SectionLabel, Breadcrumbs, CTASection, FlakeSwatch } from "../components/Shared";
import { PROCESS_PHASES, COMPARISON_ROWS, FLAKE_COLORS, IMAGES } from "../data/siteData";

const crumbs = [{ name: "Home", path: "/" }, { name: "Services", path: "/services" }];

export default function ServicesPage() {
  return (
    <>
      <SEO
        title="TRI-BOND™ Polyaspartic Garage Floor Coating System | Vantyx Coatings"
        description="The TRI-BOND™ 3-phase polyaspartic garage floor system: diamond-ground LOCK prep, full-flake polyaspartic base, UV-stable topcoat. Compare polyaspartic vs epoxy and explore 7 flake colors."
        path="/services"
        ogImage={IMAGES.garage2}
        schemas={[serviceSchema("Polyaspartic Garage Floor Coating"), breadcrumbSchema(crumbs), localBusinessSchema()]}
      />

      <section className="hero-gradient py-20 md:py-28 px-6 md:px-12 grain relative" data-testid="services-hero">
        <div className="max-w-7xl mx-auto">
          <Breadcrumbs items={crumbs} />
          <SectionLabel>The System</SectionLabel>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white max-w-4xl">
            TRI-BOND™ — Three Phases. One Day. <span className="text-[#028090]">A Floor for Life.</span>
          </h1>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mt-6 max-w-2xl">
            Most coating failures come from shortcuts you can't see. TRI-BOND™ eliminates every one of them — mechanically, chemically, and permanently.
          </p>
        </div>
      </section>

      {/* 3 PHASES */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-12" data-testid="process-section">
        <div className="max-w-7xl mx-auto">
          <SectionLabel>How It's Built</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#000000]">The 3-Phase TRI-BOND™ Process</h2>
          <div className="mt-14 space-y-6">
            {PROCESS_PHASES.map((p) => (
              <div key={p.num} className="grid md:grid-cols-[120px_1fr] gap-6 border border-gray-200 p-8 md:p-10 hover:border-[#028090] transition-colors duration-300" data-testid={`process-phase-${p.num}`}>
                <span className="font-display text-5xl md:text-6xl font-extrabold text-[#028090]/25">{p.num}</span>
                <div>
                  <h3 className="font-display text-2xl font-bold text-[#000000] mb-3">{p.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="bg-[#000000] py-24 md:py-32 px-6 md:px-12" data-testid="comparison-section">
        <div className="max-w-5xl mx-auto">
          <SectionLabel>The Honest Comparison</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Polyaspartic vs. Epoxy</h2>
          <p className="text-gray-400 mb-12 max-w-2xl">We win every column. That's not bravado — it's chemistry. <Link to="/blog/polyaspartic-vs-epoxy-garage-floor-coating-phoenix" className="text-[#028090] underline underline-offset-2">Read the full breakdown</Link>.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm md:text-base border-collapse" data-testid="comparison-table">
              <thead>
                <tr>
                  <th className="text-left p-4 text-gray-400 font-semibold border-b border-white/15">Factor</th>
                  <th className="text-left p-4 text-gray-400 font-semibold border-b border-white/15">Standard Epoxy</th>
                  <th className="text-left p-4 text-white font-bold border-b-2 border-[#028090] bg-[#028090]/10">TRI-BOND™ Polyaspartic</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((r) => (
                  <tr key={r.factor} className="border-b border-white/10">
                    <td className="p-4 text-white font-semibold">{r.factor}</td>
                    <td className="p-4 text-gray-400"><span className="flex items-start gap-2"><XIcon size={16} className="text-red-400 mt-0.5 shrink-0" /> {r.epoxy}</span></td>
                    <td className="p-4 text-gray-200 bg-[#028090]/10"><span className="flex items-start gap-2"><Check size={16} className="text-[#028090] mt-0.5 shrink-0" /> {r.poly}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FLAKE COLORS */}
      <section className="bg-white py-24 md:py-32 px-6 md:px-12" data-testid="flake-colors-section">
        <div className="max-w-7xl mx-auto">
          <SectionLabel>Pick Your Finish</SectionLabel>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#000000] mb-4">7 Premium Flake Blends</h2>
          <p className="text-gray-500 max-w-2xl mb-12">Every blend is broadcast to full coverage and locked under a UV-stable clear topcoat — the colors you see are the colors you keep. Need help choosing? <Link to="/blog/best-garage-floor-coating-colors-arizona" className="text-[#028090] font-semibold underline underline-offset-2">Read our Arizona color guide</Link>.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {FLAKE_COLORS.map((f) => (
              <div key={f.name} className="border border-gray-200 hover:-translate-y-1 transition-transform duration-300">
                <FlakeSwatch flake={f} />
                <div className="p-4">
                  <h3 className="font-display font-bold text-[#000000]">{f.name}</h3>
                  <p className="text-gray-500 text-xs mt-1 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WARRANTY */}
      <section className="bg-gray-50 py-24 md:py-32 px-6 md:px-12" data-testid="warranty-section">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
          <div>
            <SectionLabel>In Writing. For Life.</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#000000] mb-6">Lifetime Limited Warranty</h2>
            <div className="space-y-4">
              {["Delamination & peeling", "Hot-tire pickup", "Yellowing, ambering & chalking", "Flake loss under normal use"].map((w) => (
                <p key={w} className="flex items-center gap-3 text-gray-700"><ShieldCheck size={20} className="text-[#028090] shrink-0" strokeWidth={1.5} /> {w}</p>
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-6">Valid for as long as you own your home. Transferable option available for sellers — ask your estimator.</p>
            <Link
              to="/warranty"
              data-testid="services-warranty-badge-cta"
              className="group inline-flex items-center gap-3 mt-6 bg-[#000000] border-2 border-[#E8B86D] text-white hover:bg-[#E8B86D] hover:text-[#000000] transition-all duration-300 pl-5 pr-6 py-3 rounded-full font-display font-bold uppercase tracking-wider text-sm shadow-lg"
            >
              <Award size={20} strokeWidth={2} className="text-[#E8B86D] group-hover:text-[#000000] transition-colors" />
              Lifetime Warranty — See Details
              <ArrowRight size={16} strokeWidth={2.5} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="/vantyx-lifetime-warranty.pdf"
              download="Vantyx-Lifetime-Limited-Warranty.pdf"
              data-testid="warranty-pdf-services"
              className="inline-flex items-center gap-2 mt-3 ml-1 text-[#028090] hover:text-[#000000] transition-colors duration-200 font-display font-bold uppercase tracking-wider text-xs underline underline-offset-4 decoration-2"
            >
              <Download size={14} strokeWidth={2.5} /> Download PDF
            </a>
          </div>
          <div className="bg-white border border-gray-200 p-8">
            <p className="flex items-center gap-2 font-display font-bold text-[#000000] mb-4"><AlertTriangle size={18} className="text-[#E8B86D]" strokeWidth={1.5} /> What's Not Covered (and why)</p>
            <ul className="space-y-3 text-sm text-gray-600">
              <li>• <strong>Structural slab movement or settling</strong> — coatings protect concrete; they can't hold a moving foundation together.</li>
              <li>• <strong>Moisture intrusion from outside the slab</strong> — e.g., flooding or irrigation failures that push water under the home.</li>
              <li>• <strong>Abuse</strong> — dragging sharp steel, welding slag burns, or chemical spills left for weeks.</li>
              <li>• <strong>Coatings installed by others</strong> — we warranty TRI-BOND™ floors we installed, top to bottom.</li>
            </ul>
            <p className="text-gray-400 text-xs mt-5">We explain every line before you sign — no fine-print games.</p>
          </div>
        </div>
      </section>

      <CTASection title="See TRI-BOND™ on Your Own Slab" subtitle="Free in-home estimate with physical flake samples. Fixed written pricing in one short visit." />
    </>
  );
}
